import type { AxiosError } from 'axios'
import { useGetPropertyDetailWithObjectReferenceId } from 'dataQueries'
import { PropertyAddressDTO, RealEstatePropertyDTO } from 'models/property'
import type { CountryCode, PropertyRef } from 'models/purchaseFlowRevamp'

import { isRoutable, missingPropertyFields } from './usePropertyCompleteness'
import { usePfEffectiveWorkspaceId } from './usePfEffectiveWorkspaceId'

export type EmbedPresetPropertyState =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'error', httpStatus?: number }
  | { status: 'ready', property: RealEstatePropertyDTO, isComplete: boolean }

/** One-line address label, in the format the property drawer shows and stores. */
export const formatPropertyAddress = (address?: PropertyAddressDTO | null): string => address
  ? [
    `${address.street ?? ''} ${address.streetNumber ?? ''}`.trim(),
    address.city,
    address.postalCode,
    address.country,
  ].filter(Boolean).join(', ')
  : ''

/**
 * The store's property reference for a property whose detail is already loaded.
 *
 * @example
 * setProperty(propertyRefFromDetail(detail))
 */
export const propertyRefFromDetail = (property: RealEstatePropertyDTO): PropertyRef => ({
  id: property.id,
  addressLabel: formatPropertyAddress(property.address),
  countryCode: (property.address?.country ?? 'DE') as CountryCode,
  regionCode: property.address?.regionCode,
  title: property.title,
  objectReferenceId: property.objectReferenceId,
  coordinates: isRoutable(property.address?.coordinates) ? property.address.coordinates : undefined,
  routingCoordinates: isRoutable(property.creativeRoutingAddress?.coordinates)
    ? property.creativeRoutingAddress!.coordinates
    : undefined,
})

/**
 * Resolves the property the CRM opened the embed for, the way legacy's
 * `PurchaseFlowRealEstateProperty` context does: Propstack's `objectReferenceId`
 * looked up in the client's workspace. No retry, as in legacy — a missing
 * property is a sync problem a retry cannot fix, and the error screen is waiting.
 *
 * @example
 * const preset = useEmbedPresetProperty(propertyRef)
 * if (preset.status === 'ready' && preset.isComplete) setProperty(propertyRefFromDetail(preset.property))
 */
export const useEmbedPresetProperty = (propertyRef: string | null): EmbedPresetPropertyState => {
  const workspaceId = usePfEffectiveWorkspaceId()
  const query = useGetPropertyDetailWithObjectReferenceId(propertyRef, workspaceId || null, false)

  if (!propertyRef) return { status: 'none' }
  if (query.isError) return { status: 'error', httpStatus: (query.error as AxiosError | null)?.response?.status }
  if (!query.data) return { status: 'loading' }

  return { status: 'ready', property: query.data, isComplete: missingPropertyFields(query.data).length === 0 }
}
